import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { userService, User, CreateUserData } from '../services/userService';
import { companyService, Company } from '../services/companyService';
import Dialog from '../components/Dialog';
import RoleGuard from '../components/RoleGuard';
import { usePermissions } from '../hooks/usePermissions';
import { Permission } from '../utils/permissions';
import TimezoneSelect from '../components/TimezoneSelect';
import { useIsMobile } from '../hooks/useIsMobile';
import './UserManagement.css';

const UserManagement: React.FC = () => {
  const { isSuperAdmin, can } = usePermissions();
  const isMobile = useIsMobile();
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    type: 'alert' | 'confirm' | 'prompt';
    variant: 'info' | 'success' | 'warning' | 'danger';
    title: string;
    message: string;
    onConfirm: (value?: string) => void;
  }>({
    isOpen: false,
    type: 'alert',
    variant: 'info',
    title: '',
    message: '',
    onConfirm: () => {}
  });
  const [filters, setFilters] = useState({
    company: '',
    role: '',
    status: ''
  });
  const [formData, setFormData] = useState<CreateUserData>({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    role: 'END_USER',
    company: '',
    status: 'ACTIVE',
    phone: '',
    timezone: 'Europe/Belgrade',
    language: 'en'
  });
  const [allowedCompanies, setAllowedCompanies] = useState<string[]>([]);
  const [canViewAllCompanies, setCanViewAllCompanies] = useState<boolean>(false);
  const [canViewInvoices, setCanViewInvoices] = useState<boolean>(false);
  const [canViewDeals, setCanViewDeals] = useState<boolean>(false);
  const [companySearchTerm, setCompanySearchTerm] = useState<string>('');
  const [formError, setFormError] = useState('');
  const [copiedUsernameId, setCopiedUsernameId] = useState('');

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, companiesData] = await Promise.all([
        userService.getUsers(filters),
        companyService.getCompanies()
      ]);
      setUsers(usersData);
      setCompanies(companiesData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    try {
      if (formData.role === 'AGENT' && !canViewAllCompanies && allowedCompanies.length === 0) {
        setFormError('Select at least one company this agent can manage.');
        return;
      }

      if (editingUser) {
        if (!can(Permission.UPDATE_USERS) && can(Permission.UPDATE_USERNAMES)) {
          await userService.updateUsername(editingUser._id, formData.username || '');
        } else {
          await userService.updateUser(editingUser._id, {
            ...formData,
            allowedCompanies: canViewAllCompanies ? [] : allowedCompanies,
            canViewAllCompanies,
            canViewInvoices: formData.role === 'CLIENT_ADMIN' ? canViewInvoices : false,
            canViewDeals: formData.role === 'CLIENT_ADMIN' ? canViewDeals : false
          });
        }
      } else {
        await userService.createUser({
          ...formData,
          allowedCompanies: canViewAllCompanies ? [] : allowedCompanies,
          canViewAllCompanies,
          canViewInvoices: formData.role === 'CLIENT_ADMIN' ? canViewInvoices : false,
          canViewDeals: formData.role === 'CLIENT_ADMIN' ? canViewDeals : false
        } as any);
      }
      await loadData();
      setShowModal(false);
      setEditingUser(null);
      resetForm();
    } catch (error: any) {
      console.error('Error saving user:', error);
      setFormError(error.response?.data?.message || 'Error saving user.');
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      username: user.username || '',
      role: user.role,
      company: user.company._id,
      status: user.status,
      phone: user.phone || '',
      timezone: user.timezone,
      language: user.language
    });
    const allowed = Array.isArray(user.allowedCompanies)
      ? user.allowedCompanies.map((company: any) => company?._id || company).filter(Boolean)
      : [];
    setAllowedCompanies(allowed);
    setCanViewAllCompanies(Boolean(user.canViewAllCompanies));
    setCanViewInvoices(Boolean(user.canViewInvoices));
    setCanViewDeals(Boolean(user.canViewDeals));
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    setDialog({
      isOpen: true,
      type: 'confirm',
      variant: 'danger',
      title: 'Delete User',
      message: 'Are you sure you want to delete this user? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await userService.deleteUser(id);
          await loadData();
          setDialog({
            isOpen: true,
            type: 'alert',
            variant: 'success',
            title: 'Success',
            message: 'User successfully deleted.',
            onConfirm: () => setDialog({ ...dialog, isOpen: false })
          });
        } catch (error) {
          console.error('Error deleting user:', error);
          setDialog({
            isOpen: true,
            type: 'alert',
            variant: 'danger',
            title: 'Error',
            message: 'Error deleting user.',
            onConfirm: () => setDialog({ ...dialog, isOpen: false })
          });
        }
      }
    });
  };

  const handleResetPassword = async (id: string) => {
    setDialog({
      isOpen: true,
      type: 'prompt',
      variant: 'warning',
      title: 'Reset Password',
      message: 'Enter new password for user:',
      onConfirm: async (newPassword?: string) => {
        if (newPassword && newPassword.trim()) {
          try {
            await userService.resetUserPassword(id, newPassword);
            setDialog({
              isOpen: true,
              type: 'alert',
              variant: 'success',
              title: 'Success',
              message: 'Password successfully reset.',
              onConfirm: () => setDialog({ ...dialog, isOpen: false })
            });
          } catch (error) {
            console.error('Error resetting password:', error);
            setDialog({
              isOpen: true,
              type: 'alert',
              variant: 'danger',
              title: 'Error',
              message: 'Error resetting password.',
              onConfirm: () => setDialog({ ...dialog, isOpen: false })
            });
          }
        } else {
          setDialog({ ...dialog, isOpen: false });
        }
      }
    });
  };

  const handleResendVerification = async (id: string) => {
    try {
      await userService.resendVerification(id);
      setDialog({
        isOpen: true,
        type: 'alert',
        variant: 'success',
        title: 'Success',
        message: 'Verification email has been resent. The link is valid for 24 hours.',
        onConfirm: () => setDialog({ ...dialog, isOpen: false })
      });
      await loadData();
    } catch (error: any) {
      setDialog({
        isOpen: true,
        type: 'alert',
        variant: 'danger',
        title: 'Error',
        message: error.response?.data?.message || 'Error sending verification email.',
        onConfirm: () => setDialog({ ...dialog, isOpen: false })
      });
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      username: '',
      role: 'END_USER',
      company: '',
      status: 'ACTIVE',
      phone: '',
      timezone: 'Europe/Belgrade',
      language: 'en'
    });
    if (!isSuperAdmin() && companies.length === 1) {
      setFormData((current) => ({ ...current, company: companies[0]._id }));
    }
    setCompanySearchTerm('');
    setAllowedCompanies([]);
    setCanViewAllCompanies(false);
    setCanViewInvoices(false);
    setCanViewDeals(false);
    setFormError('');
  };

  const filteredCompanies = companies.filter((company) =>
    !company.isOwner &&
    company.type !== 'SERVICE_PROVIDER' &&
    company.name.toLowerCase().includes(companySearchTerm.toLowerCase())
  );
  const belongsToSystem = formData.role === 'AGENT' || formData.role === 'SUPER_ADMIN';

  const getRoleLabel = (role: string) => {
    const labels = {
      'SUPER_ADMIN': 'Super Administrator',
      'AGENT': 'Agent',
      'CLIENT_ADMIN': 'Client Administrator',
      'END_USER': 'End User'
    };
    return labels[role as keyof typeof labels] || role;
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      'ACTIVE': 'Active',
      'INACTIVE': 'Inactive',
      'SUSPENDED': 'Suspended'
    };
    return labels[status as keyof typeof labels] || status;
  };

  const getVerificationLabel = (verified?: boolean) => {
    return verified ? 'Verified' : 'Pending';
  };

  const userActions = (user: User) => (
    <div className="action-buttons">
      <RoleGuard permissions={[Permission.UPDATE_USERS, Permission.UPDATE_USERNAMES]}>
        <Button
          icon="pi pi-pencil"
          className="p-button-secondary p-button-sm p-button-rounded compact-action-btn"
          onClick={() => handleEdit(user)}
          tooltip={can(Permission.UPDATE_USERS) ? 'Edit' : 'Change username'}
          tooltipOptions={{ position: 'top' }}
        />
      </RoleGuard>
      <RoleGuard permissions={[Permission.RESET_PASSWORDS]}>
        <Button
          icon="pi pi-key"
          className="p-button-warning p-button-sm p-button-rounded compact-action-btn"
          onClick={() => handleResetPassword(user._id)}
          tooltip="Reset Password"
          tooltipOptions={{ position: 'top' }}
        />
      </RoleGuard>
      {!user.emailVerified && (
        <RoleGuard permissions={[Permission.UPDATE_USERS]}>
          <Button
            icon="pi pi-envelope"
            className="p-button-info p-button-sm p-button-rounded compact-action-btn"
            onClick={() => handleResendVerification(user._id)}
            tooltip="Resend Verification"
            tooltipOptions={{ position: 'top' }}
          />
        </RoleGuard>
      )}
      <RoleGuard permissions={[Permission.DELETE_USERS]}>
        <Button
          icon="pi pi-trash"
          className="p-button-danger p-button-sm p-button-rounded compact-action-btn"
          onClick={() => handleDelete(user._id)}
          tooltip="Delete"
          tooltipOptions={{ position: 'top' }}
        />
      </RoleGuard>
    </div>
  );

  const copyUsername = async (userId: string, username?: string) => {
    if (!username) {
      return;
    }

    try {
      await navigator.clipboard.writeText(username);
      setCopiedUsernameId(userId);
      window.setTimeout(() => setCopiedUsernameId(''), 1500);
    } catch (error) {
      console.error('Copy username failed', error);
    }
  };

  const usernameOnlyEdit = Boolean(
    editingUser && !can(Permission.UPDATE_USERS) && can(Permission.UPDATE_USERNAMES)
  );

  if (loading) {
    return <>
      {/* Global spinner while loading */}
      <div className="spinner-wrapper">
        <div className="spinner">
          <div className="sk-folding-cube">
            <div className="sk-cube1 sk-cube"></div>
            <div className="sk-cube2 sk-cube"></div>
            <div className="sk-cube4 sk-cube"></div>
            <div className="sk-cube3 sk-cube"></div>
          </div>
        </div>
      </div>
    </>;
  }

  return (
    <div className="user-management">
      <div className="page-header">
        <div>
          <h1 className="page-title">User Management</h1>
          <p className="page-subtitle">Create users and manage roles, status, username and access</p>
        </div>
        <RoleGuard permissions={[Permission.CREATE_USERS]}>
          <Button
            label="Add User"
            icon="pi pi-user-plus"
            className="new-ticket-btn"
            onClick={() => {
              setEditingUser(null);
              resetForm();
              setShowModal(true);
            }}
          />
        </RoleGuard>
      </div>

      <div className="filters">
        <div className="filter-group">
          <label>Company:</label>
          <select
            value={filters.company}
            onChange={(e) => setFilters({...filters, company: e.target.value})}
          >
            <option value="">All companies</option>
            {companies.map(company => (
              <option key={company._id} value={company._id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Role:</label>
          <select
            value={filters.role}
            onChange={(e) => setFilters({...filters, role: e.target.value})}
          >
            <option value="">All roles</option>
            <option value="END_USER">End User</option>
            <option value="CLIENT_ADMIN">Client Administrator</option>
            {isSuperAdmin() && (
              <>
                <option value="AGENT">Agent</option>
                <option value="SUPER_ADMIN">Super Administrator</option>
              </>
            )}
          </select>
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters({...filters, status: e.target.value})}
          >
            <option value="">All statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="SUSPENDED">Suspended</option>
          </select>
        </div>
      </div>

      {isMobile ? (
        <div className="list-cards">
          {users.map((user) => (
            <div key={user._id} className="list-card">
              <div className="list-card-header">
                <div>
                  <div className="list-card-title">{user.firstName} {user.lastName}</div>
                  <div className="list-card-sub">{user.email}</div>
                </div>
                <span className={`status-badge status-${user.status.toLowerCase()}`}>
                  {getStatusLabel(user.status)}
                </span>
              </div>
              <div className="list-card-meta">
                <span className={`role-badge role-${user.role.toLowerCase()}`}>{getRoleLabel(user.role)}</span>
                <span>{user.company.name}</span>
                {user.username && <span>@{user.username}</span>}
                <span className={`verification-badge ${user.emailVerified ? 'verified' : 'pending'}`}>
                  {getVerificationLabel(user.emailVerified)}
                </span>
              </div>
              <div className="list-card-actions">
                {userActions(user)}
              </div>
            </div>
          ))}
          {users.length === 0 && (
            <div className="mobile-empty-state">
              <i className="pi pi-users" />
              <h3>No users</h3>
              <p>Nothing matches these filters.</p>
            </div>
          )}
        </div>
      ) : (
      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Username</th>
              <th>Company</th>
              <th>Role</th>
              <th>Status</th>
              <th>Verification</th>
              <th>Phone</th>
              <th>Akcije</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user._id}>
                <td>{user.firstName} {user.lastName}</td>
                <td>{user.email}</td>
                <td>
                  {user.username ? (
                    <div className="username-cell">
                      <code>{user.username}</code>
                      <Button
                        type="button"
                        icon={copiedUsernameId === user._id ? 'pi pi-check' : 'pi pi-copy'}
                        className="p-button-text p-button-sm username-copy-btn"
                        onClick={() => copyUsername(user._id, user.username)}
                        tooltip={copiedUsernameId === user._id ? 'Copied' : 'Copy username'}
                        tooltipOptions={{ position: 'top' }}
                      />
                    </div>
                  ) : (
                    '-'
                  )}
                </td>
                <td>{user.company.name}</td>
                <td>
                  <span className={`role-badge role-${user.role.toLowerCase()}`}>
                    {getRoleLabel(user.role)}
                  </span>
                </td>
                <td>
                  <span className={`status-badge status-${user.status.toLowerCase()}`}>
                    {getStatusLabel(user.status)}
                  </span>
                </td>
                <td>
                  <span className={`verification-badge ${user.emailVerified ? 'verified' : 'pending'}`}>
                    {getVerificationLabel(user.emailVerified)}
                  </span>
                </td>
                <td>{user.phone || '-'}</td>
                <td>
                  {userActions(user)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      <Dialog
        isOpen={dialog.isOpen}
        type={dialog.type}
        variant={dialog.variant}
        title={dialog.title}
        message={dialog.message}
        confirmText={
          dialog.type === 'confirm' 
            ? 'Delete' 
            : dialog.type === 'prompt' 
            ? 'Reset' 
            : 'OK'
        }
        cancelText="Cancel"
        placeholder={dialog.type === 'prompt' ? 'Enter new password...' : ''}
        onConfirm={dialog.onConfirm}
        onCancel={() => setDialog({ ...dialog, isOpen: false })}
      />

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <div className="modal-header-icon">
                <i className={editingUser ? 'pi pi-user-edit' : 'pi pi-user-plus'}></i>
              </div>
              <div className="app-modal-header-text">
                <h2>{usernameOnlyEdit ? 'Change Username' : editingUser ? 'Edit User' : 'Add User'}</h2>
                <p>
                  {usernameOnlyEdit
                    ? 'The user can set their username only once. A supervisor can change it at any time.'
                    : editingUser
                    ? 'Update user details and permissions'
                    : 'Fill out the form to create a new user'}
                </p>
              </div>
              <button
                type="button"
                className="close-btn"
                onClick={() => setShowModal(false)}
                aria-label="Close"
              >
                <i className="pi pi-times"></i>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              {formError && <div className="form-error">{formError}</div>}

              {!usernameOnlyEdit && (
              <>
              <div className="form-row">
                <div className="form-group">
                  <label><i className="pi pi-user"></i> First Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    placeholder="First name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label><i className="pi pi-user"></i> Last Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    placeholder="Last name"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label><i className="pi pi-envelope"></i> Email <span className="required">*</span></label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="user@email.com"
                  required
                />
              </div>
              </>
              )}

              <div className="form-group">
                <label><i className="pi pi-at"></i> Username</label>
                <input
                  type="text"
                  value={formData.username || ''}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase() })}
                  placeholder="username"
                  required={usernameOnlyEdit}
                />
                <small className="field-hint">
                  {editingUser
                    ? 'The user can set their username only once. A supervisor (client admin, agent, or superadmin) can change it at any time.'
                    : 'Optional. If left empty, the user can set it once on their profile.'}
                </small>
              </div>

              {!usernameOnlyEdit && !editingUser && (
                <div className="form-group">
                  <label><i className="pi pi-envelope"></i> Account activation</label>
                  <div className="field-hint">
                    After creation, the user receives an email verification link and sets their own password.
                  </div>
                </div>
              )}

              {!usernameOnlyEdit && (
              <>
              <div className="form-row">
                <div className="form-group">
                  <label><i className="pi pi-id-card"></i> Role <span className="required">*</span></label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value as any})}
                    required
                  >
                    <option value="END_USER">End User</option>
                    <option value="CLIENT_ADMIN">Client Administrator</option>
                    {isSuperAdmin() && (
                      <>
                        <option value="AGENT">Agent</option>
                        <option value="SUPER_ADMIN">Super Administrator</option>
                      </>
                    )}
                  </select>
                </div>

                {!belongsToSystem && (
                <div className="form-group">
                  <label><i className="pi pi-building"></i> Company <span className="required">*</span></label>
                  <select
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    required
                  >
                    <option value="">Select company</option>
                    {companies.map(company => (
                      <option key={company._id} value={company._id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
                )}
              </div>

              {isSuperAdmin() && formData.role === 'CLIENT_ADMIN' && (
                <div className="agent-visibility-section">
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={canViewInvoices}
                        onChange={(e) => setCanViewInvoices(e.target.checked)}
                        className="checkbox-input"
                      />
                      <span className="checkbox-text">Can view invoices</span>
                    </label>
                    <small className="field-hint">When enabled, this client admin can see invoices for their company</small>
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={canViewDeals}
                        onChange={(e) => setCanViewDeals(e.target.checked)}
                        className="checkbox-input"
                      />
                      <span className="checkbox-text">Can view deals</span>
                    </label>
                    <small className="field-hint">When enabled, this client admin can see retainers and packages for their company</small>
                  </div>
                </div>
              )}

              {/* Agent company visibility settings */}
              {formData.role === 'AGENT' && (
                <div className="agent-visibility-section">
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={canViewAllCompanies}
                        onChange={(e) => setCanViewAllCompanies(e.target.checked)}
                        className="checkbox-input"
                      />
                      <span className="checkbox-text">Can manage tickets for all companies</span>
                    </label>
                    <small className="field-hint">When enabled, this agent can work on tickets from every client company</small>
                  </div>
                  
                  {!canViewAllCompanies && (
                    <div className="form-group">
                      <label>Companies this agent can manage</label>
                      <div className="company-search-container">
                        <input
                          type="text"
                          placeholder="Search companies..."
                          value={companySearchTerm}
                          onChange={(e) => setCompanySearchTerm(e.target.value)}
                          className="company-search-input"
                        />
                      </div>
                      <div className="companies-checkbox-list">
                        {filteredCompanies.map(company => (
                          <label key={company._id} className="company-checkbox-item">
                            <input
                              type="checkbox"
                              checked={allowedCompanies.includes(company._id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setAllowedCompanies([...allowedCompanies, company._id]);
                                } else {
                                  setAllowedCompanies(allowedCompanies.filter(id => id !== company._id));
                                }
                              }}
                              className="company-checkbox"
                            />
                            <span className="company-name">{company.name}</span>
                          </label>
                        ))}
                        {filteredCompanies.length === 0 && companySearchTerm && (
                          <div className="no-companies-found">
                            No companies found matching "{companySearchTerm}"
                          </div>
                        )}
                      </div>
                      <small className="field-hint">Pick the client companies whose tickets this agent can work on</small>
                    </div>
                  )}
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label><i className="pi pi-info-circle"></i> Status <span className="required">*</span></label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value as any})}
                    required
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                    <option value="SUSPENDED">Suspended</option>
                  </select>
                </div>

                <div className="form-group">
                  <label><i className="pi pi-phone"></i> Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="Phone number"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label><i className="pi pi-globe"></i> Timezone</label>
                  <TimezoneSelect
                    value={formData.timezone}
                    onChange={(timezone) => setFormData({ ...formData, timezone })}
                  />
                  <small className="field-hint">Open the field and pick a city, or search.</small>
                </div>
              </div>
              </>
              )}

              <div className="modal-actions">
                <Button
                  type="button"
                  label="Cancel"
                  icon="pi pi-times"
                  className="cancel-btn p-button-secondary"
                  onClick={() => setShowModal(false)}
                />
                <Button
                  type="submit"
                  label={usernameOnlyEdit ? 'Save Username' : editingUser ? 'Save Changes' : 'Create User'}
                  icon="pi pi-check"
                  className="submit-btn"
                />
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
